// required for polyfills for old browsers
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'whatwg-fetch';

import Vue from "vue";
import VueI18n from "vue-i18n";
import { BootstrapVue, IconsPlugin } from "bootstrap-vue";
import VueHtmlToPaper from "vue-html-to-paper";

import "./main.scss";
import App from "@/app/app.vue";
import router from "@/app/app-routes";
import { DateHelper } from "@/app/shared/services/helper/date-helper";
import { ServiceWorkerHelper } from "@/app/shared/services/helper/service-worker-helper";
import { deploymentService } from "@/app/shared/services/deployment-service/deployment-service";
import { environment } from "./environment/environment";

import { appLocalStorage } from "./app/shared/services/app-storage/app-storage";

import { themeService } from './app/shared/services/theme-service/theme-service';
import { whiteLabelingService } from './app/shared/services/white-labeling-service/white-labeling-service';
import { BaseComponent } from "@/app/shared/components/base-component/base-component";

const VueHtmlToPaperOptions = {
  name: '_blank',
  specs: [
    'fullscreen=yes',
    'titlebar=yes',
    'scrollbars=yes'
  ],
  styles: [
    'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css',
    'https://unpkg.com/kidlat-css/css/kidlat.css'
  ],
  timeout: 100, // default timeout before the print window appears
  autoClose: true, // if false, the window will not close after printing
  windowTitle: window.document.title, // override the window title
}

const getCurrentLocale = (): Locale => {
  try {
    let lang = appLocalStorage.getItem("locale") || navigator.language.split("-")[0].toLowerCase();
    return supportedLocales.includes(lang) ? lang : "en";
  } catch (e) {
    console.error("Cannot get locale. Fallback to 'en'");
    console.error(e);
  }

  return "en";
}

Vue.use(BootstrapVue);
Vue.use(IconsPlugin);
Vue.use(VueI18n);
Vue.use(VueHtmlToPaper, VueHtmlToPaperOptions);

Vue.config.productionTip = false;

Vue.mixin({
  methods: {
    ...DateHelper.prototype,
  },
});

// Vue 2 tears a component down with "beforeDestroy" - "unmounted" is a Vue 3 hook and never runs
// here. vue-class-component turns an "unmounted()" class method into an ordinary method instead,
// so every component that puts its teardown there (unregistering from the singleton event
// services, unloading map layers, removing window listeners) silently never cleaned up and stayed
// reachable from those singletons for the lifetime of the tab. Forward the hook so that teardown
// actually runs. Components that need Vue 2 semantics explicitly can still define "beforeDestroy".
Vue.mixin({
  beforeDestroy(this: Vue) {
    if (!(this instanceof BaseComponent)) {
      return; // third-party components (bootstrap-vue, ...) never mean our teardown by "unmounted"
    }

    const unmounted = (this as unknown as { unmounted?: unknown }).unmounted;
    if (typeof unmounted !== "function") {
      return;
    }

    try {
      // Most of them are async. A failing teardown must not abort the rest of the destroy phase.
      const teardown = unmounted.call(this);
      if (teardown instanceof Promise) {
        teardown.catch(e => console.error(e));
      }
    } catch (e) {
      console.error(e);
    }
  },
});

export const supportedLocales = ["en", "de", "fr", "es", "pt", "hr", "zh", "ko", "cs"] as const;
export type Locale = typeof supportedLocales[number];
export const currentLocale = getCurrentLocale();

// ML-498: the ml-training admin page ships its keys in a sibling JSON
// file rather than touching the main locale bundles. Spread the
// supplemental file on top of the base locale; later commits can fold
// these keys into en.json/es.json/pt.json and drop the supplement.
export const i18n = new VueI18n({
  locale: currentLocale,
  fallbackLocale: "en",
  messages: {
    en: require("./locales/en.json"),
    de: require("./locales/de.json"),
    fr: require("./locales/fr.json"),
    es: require("./locales/es.json"),
    pt: require("./locales/pt.json"),
    hr: require("./locales/hr.json"),
    zh: require("./locales/zh.json"),
    ko: require("./locales/ko.json"),
    cs: require("./locales/cs.json"),
  },
});


themeService.updateTheme();

try {
  await whiteLabelingService.appendStyles();
} catch (e) {
  // Default colors are better than an app that never mounts
  console.error(e);
}

new Vue({
  router,
  i18n,
  render: h => h(App),
}).$mount("#app");

// Start watching for new deployments as early as possible: the sooner a stale tab knows
// that it runs an outdated build, the higher the chance that it can reload unnoticed.
deploymentService.start();

// Register the service worker for PWA on production only
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      if (environment === "production" || environment === "staging") {
        await ServiceWorkerHelper.registerServiceWorker(buildId => deploymentService.onServiceWorkerBuild(buildId));
      }
    } catch (err) {
      console.error('Service worker registration failed: ', err);
    }
  });
}
